import styled from "styled-components";
import {
  BsCheckCircleFill,
  BsFileEarmarkPdf,
  BsTelephone,
  BsWhatsapp,
  BsEnvelope,
  BsArrowRight,
} from "react-icons/bs";
import { CardComp, ButtonComp } from "../..";
import { investorButtonProps } from "../investorButtonStyles";
import InvestmentDetailSection from "./InvestmentDetailSection";
import InvestmentFaqList from "./InvestmentFaqList";
import { toast } from "react-hot-toast";

const StoryBlock = styled.div`
  padding: 1.25rem 0;
  border-bottom: 1px solid #f1f1f1;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  h3 {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--color-primary);
    margin-bottom: 0.5rem;
  }

  p {
    color: #45464e;
    line-height: 1.75;
    margin-bottom: 0;
    font-size: 0.95rem;
  }
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 2rem;

  &::before {
    content: "";
    position: absolute;
    left: 11px;
    top: 8px;
    bottom: 8px;
    width: 2px;
    background: linear-gradient(180deg, var(--color-primary) 0%, var(--color-secondary) 100%);
    opacity: 0.35;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 1.75rem;

  &:last-child {
    padding-bottom: 0;
  }

  .dot {
    position: absolute;
    left: -2rem;
    top: 4px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #fff;
    border: 3px solid var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    font-weight: 700;
    color: var(--color-primary);
    z-index: 1;
  }

  .step-title {
    font-weight: 600;
    color: #383838;
    margin-bottom: 0.25rem;
  }

  .step-desc {
    font-size: 0.875rem;
    color: #525252;
    line-height: 1.6;
    margin-bottom: 0;
  }
`;

const HighlightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;

  @media (min-width: 576px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const HighlightItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 1rem;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  font-size: 0.875rem;
  font-weight: 500;
  color: #383838;
  line-height: 1.4;

  svg {
    color: #19bc31;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const GalleryScroll = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d0d0d0;
    border-radius: 3px;
  }

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    overflow: visible;
  }

  @media (min-width: 992px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const GalleryItem = styled.figure`
  flex: 0 0 75%;
  scroll-snap-align: start;
  margin: 0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

  @media (min-width: 768px) {
    flex: unset;
  }

  img {
    width: 100%;
    height: 160px;
    object-fit: cover;
    display: block;
  }

  figcaption {
    padding: 0.6rem 0.75rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: #525252;
    background: #fff;
  }
`;

const MilestoneTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: 14px;
      left: 5%;
      right: 5%;
      height: 2px;
      background: #e5e5e5;
      z-index: 0;
    }
  }
`;

const Milestone = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 0;
  position: relative;

  @media (min-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    flex: 1;
    padding: 0 0.5rem;
  }

  .m-dot {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    font-weight: 700;
    z-index: 1;
    background: ${(p) =>
      p.$status === "completed" ? "var(--color-primary)" : p.$status === "current" ? "var(--color-secondary)" : "#e5e5e5"};
    color: ${(p) => (p.$status === "upcoming" ? "#525252" : "#fff")};
    border: 3px solid #fff;
    box-shadow: 0 0 0 1px #e5e5e5;
  }

  .m-label {
    font-weight: 600;
    font-size: 0.85rem;
    color: #383838;
    margin-bottom: 0.15rem;
  }

  .m-date {
    font-size: 0.75rem;
    color: #525252;
  }
`;

const TermCard = styled(CardComp)`
  && {
    border: none;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
    height: 100%;
    background: #fff;
  }

  .term-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #525252;
    font-weight: 600;
    margin-bottom: 0.35rem;
  }

  .term-value {
    font-size: 0.95rem;
    font-weight: 600;
    color: #383838;
  }
`;

const DocCard = styled(CardComp)`
  && {
    border: 1px solid #e8e8e8;
    border-radius: 14px;
    height: 100%;
    transition: border-color 0.2s ease;

    &:hover {
      border-color: var(--color-primary-soft);
    }
  }

  .pdf-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: var(--color-primary-soft);
    color: var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.35rem;
    margin-bottom: 0.75rem;
  }
`;

const UpdateItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #f1f1f1;

  &:last-child {
    border-bottom: none;
  }

  .update-date {
    font-size: 0.75rem;
    color: #525252;
    min-width: 88px;
    flex-shrink: 0;
  }

  .update-text {
    font-size: 0.9rem;
    color: #383838;
    font-weight: 500;
  }
`;

const SupportCard = styled(CardComp)`
  && {
    border: none;
    border-radius: 14px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    text-align: center;
    height: 100%;
    width: 100%;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  && .card-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    padding: 1.25rem 1rem;
  }

  .support-body {
    flex: 1 1 auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    min-height: 4.5rem;
  }

  .support-action {
    width: 100%;
    margin-top: auto;
  }
`;

export default function InvestmentDetailPremiumContent({ opportunity }) {
  if (!opportunity) return null;

  const support = opportunity.support || opportunity.supportContact || {};
  const hasSupportContact = Boolean(support.phone || support.email || support.whatsapp);
  const openSupport = (type) => {
    if (type === "Call" && support.phone) {
      window.location.href = `tel:${support.phone}`;
      return;
    }
    if (type === "WhatsApp" && support.whatsapp) {
      window.open(`https://wa.me/${String(support.whatsapp).replace(/\D/g, "")}`, "_blank", "noopener,noreferrer");
      return;
    }
    if (type === "Email" && support.email) {
      window.location.href = `mailto:${support.email}`;
      return;
    }
    toast.error("Contact details are not available.");
  };

  const openDocument = (doc) => {
    if (doc?.url) {
      window.open(doc.url, "_blank", "noopener,noreferrer");
      return;
    }
    toast.error("Document preview is not available yet.");
  };

  const display = (value, fallback = "—") => {
    if (value === null || value === undefined || value === "" || Number.isNaN(value)) return fallback;
    return value;
  };

  const viewableDocuments = (opportunity.documents || []).filter((doc) => Boolean(doc?.url));

  const termCards = [
    { label: "Expected return", value: `${display(opportunity.expectedReturnPct, 0)}% p.a.` },
    {
      label: "Duration",
      value: opportunity.durationMonths ? `${opportunity.durationMonths} months` : "—",
    },
    { label: "Minimum units", value: display(opportunity.minUnitsPurchase) },
    { label: "Maximum units", value: display(opportunity.maxUnitsPurchase) },
    { label: "Risk level", value: display(opportunity.riskLevel) },
    { label: "Repayment type", value: display(opportunity.payoutType || opportunity.repaymentPlan) },
    { label: "Currency", value: display(opportunity.currency, "NGN") },
    { label: "Early exit policy", value: display(opportunity.earlyExitPolicy, "Not specified") },
  ];

  const overview = opportunity.overview || {};
  const overviewBlocks = [
    { title: "What is this investment?", body: overview.what || opportunity.description },
    { title: "Why does it exist?", body: overview.why },
    { title: "How is your money used?", body: overview.useOfFunds || opportunity.fundsUsage },
    { title: "What is the expected outcome?", body: overview.outcome },
  ].filter((block) => Boolean(block.body));

  const supportCards = [
    support.phone
      ? {
          key: "call",
          icon: <BsTelephone size={24} className="mb-2" style={{ color: "var(--color-primary)" }} aria-hidden />,
          label: "Call",
          detail: support.phone,
          btnText: "Call now",
          type: "Call",
        }
      : null,
    support.whatsapp
      ? {
          key: "whatsapp",
          icon: <BsWhatsapp size={24} className="mb-2" style={{ color: "#19bc31" }} aria-hidden />,
          label: "WhatsApp",
          detail: support.whatsapp,
          btnText: "Open WhatsApp",
          type: "WhatsApp",
        }
      : null,
    support.email
      ? {
          key: "email",
          icon: <BsEnvelope size={24} className="mb-2" style={{ color: "var(--color-secondary)" }} aria-hidden />,
          label: "Email",
          detail: support.email,
          btnText: "Send email",
          type: "Email",
        }
      : null,
    support.phone
      ? {
          key: "callback",
          icon: <BsArrowRight size={24} className="mb-2" style={{ color: "var(--color-primary)" }} aria-hidden />,
          label: "Callback",
          detail: "Request a call",
          btnText: "Request callback",
          type: "Call",
        }
      : null,
  ].filter(Boolean);

  return (
    <>
      <InvestmentDetailSection
        id="overview"
        eyebrow="The opportunity"
        title="Investment overview"
        subtitle="Understand what you are investing in before you commit."
      >
        <CardComp cardClassName="border-0 shadow-sm rounded-4">
          {overviewBlocks.length > 0 ? (
            overviewBlocks.map((block) => (
              <StoryBlock key={block.title}>
                <h3>{block.title}</h3>
                <p>{block.body}</p>
              </StoryBlock>
            ))
          ) : (
            <StoryBlock>
              <h3>What is this investment?</h3>
              <p>{opportunity.description || "Overview content will appear here once published."}</p>
            </StoryBlock>
          )}
        </CardComp>
      </InvestmentDetailSection>

      <InvestmentDetailSection
        id="how-it-works"
        eyebrow="Process"
        title="How it works"
        subtitle="A clear path from interest to maturity — no surprises."
      >
        <CardComp cardClassName="border-0 shadow-sm rounded-4">
          <Timeline role="list" aria-label="Investment process steps">
            {opportunity.howItWorks?.map((step) => (
              <TimelineItem key={step.step} role="listitem">
                <span className="dot" aria-hidden>
                  {step.step}
                </span>
                <p className="step-title">{step.title}</p>
                <p className="step-desc">{step.description}</p>
              </TimelineItem>
            ))}
          </Timeline>
        </CardComp>
      </InvestmentDetailSection>

      <InvestmentDetailSection
        id="highlights"
        eyebrow="Trust"
        title="Investment highlights"
        subtitle="Signals that help you evaluate quality and transparency."
      >
        <HighlightGrid>
          {opportunity.highlights?.map((item) => (
            <HighlightItem key={item}>
              <BsCheckCircleFill aria-hidden />
              <span>{item}</span>
            </HighlightItem>
          ))}
        </HighlightGrid>
      </InvestmentDetailSection>

      <InvestmentDetailSection
        id="gallery"
        eyebrow="On the ground"
        title="Gallery"
        subtitle="Visual context from sites, assets, and operations."
      >
        <GalleryScroll>
          {opportunity.gallery?.map((item) => (
            <GalleryItem key={item.label}>
              <img src={item.url} alt={item.label} loading="lazy" />
              <figcaption>{item.label}</figcaption>
            </GalleryItem>
          ))}
        </GalleryScroll>
      </InvestmentDetailSection>

      <InvestmentDetailSection
        id="timeline"
        eyebrow="Milestones"
        title="Investment timeline"
        subtitle="See when funding closes, when your investment starts, and when returns are expected."
      >
        <CardComp cardClassName="border-0 shadow-sm rounded-4">
          <MilestoneTimeline role="list" aria-label="Funding and maturity timeline">
            {opportunity.fundingTimeline?.map((m) => (
              <Milestone key={m.label} $status={m.status} role="listitem">
                <span className="m-dot" aria-hidden>
                  {m.status === "completed" ? "✓" : ""}
                </span>
                <div>
                  <p className="m-label mb-0">{m.label}</p>
                  <p className="m-date mb-0">{m.date}</p>
                </div>
              </Milestone>
            ))}
          </MilestoneTimeline>
        </CardComp>
      </InvestmentDetailSection>

      <InvestmentDetailSection
        id="terms"
        eyebrow="Legal & financial"
        title="Investment terms"
        subtitle="Key terms presented clearly — full details in your memorandum."
      >
        <div className="row g-3">
          {termCards.map((t) => (
            <div key={t.label} className="col-6 col-md-3">
              <TermCard cardClassName="h-100">
                <p className="term-label mb-0">{t.label}</p>
                <p className="term-value mb-0">{t.value}</p>
              </TermCard>
            </div>
          ))}
        </div>
      </InvestmentDetailSection>

      {viewableDocuments.length > 0 ? (
        <InvestmentDetailSection
          id="documents"
          eyebrow="Due diligence"
          title="Documents"
          subtitle="Review supporting materials before you submit a request."
        >
          <div className="row g-3">
            {viewableDocuments.map((doc) => (
              <div key={doc.id} className="col-12 col-sm-6 col-lg-4">
                <DocCard cardClassName="h-100 d-flex flex-column">
                  <div className="pdf-icon" aria-hidden>
                    <BsFileEarmarkPdf />
                  </div>
                  <h6 className="fw-bold mb-1">{doc.title}</h6>
                  <p className="text-muted small flex-grow-1 mb-3">{doc.description || doc.type || "Document"}</p>
                  <ButtonComp
                    btnText="View document"
                    onClick={() => openDocument(doc)}
                    {...investorButtonProps.outline}
                    ButtonInlineStyle={{
                      ...investorButtonProps.outline.ButtonInlineStyle,
                      fontSize: "0.8rem",
                      minHeight: 40,
                    }}
                  />
                </DocCard>
              </div>
            ))}
          </div>
        </InvestmentDetailSection>
      ) : null}

      <InvestmentDetailSection
        id="updates"
        eyebrow="Activity"
        title="Latest updates"
        subtitle="Recent progress on this opportunity."
      >
        <CardComp cardClassName="border-0 shadow-sm rounded-4">
          {opportunity.updates?.map((u) => (
            <UpdateItem key={u.id}>
              <span className="update-date">{u.date}</span>
              <span className="update-text">{u.text}</span>
            </UpdateItem>
          ))}
        </CardComp>
      </InvestmentDetailSection>

      <InvestmentDetailSection
        id="faqs"
        eyebrow="Questions"
        title="Frequently asked questions"
      >
        <CardComp cardClassName="border-0 shadow-sm rounded-4 px-3">
          <InvestmentFaqList faqs={opportunity.faqs} />
        </CardComp>
      </InvestmentDetailSection>

      <InvestmentDetailSection
        id="risk"
        eyebrow="Transparency"
        title="Risk & disclaimer"
      >
        <CardComp
          cardClassName="border-0 rounded-4"
          style={{
            background: "linear-gradient(135deg, var(--color-primary-soft) 0%, rgba(49,119,140,0.08) 100%)",
            border: "1px solid #e8e8e8",
          }}
        >
          <p className="mb-0" style={{ lineHeight: 1.8, color: "#45464e", fontSize: "0.95rem" }}>
            {opportunity.riskDisclaimer}
          </p>
        </CardComp>
      </InvestmentDetailSection>

      {hasSupportContact ? (
        <InvestmentDetailSection
          id="support"
          eyebrow="We're here to help"
          title="Need help?"
          subtitle="Our investor relations team is available to answer questions about this opportunity."
        >
          <div className="row g-3 align-items-stretch">
            {supportCards.map((card) => (
              <div key={card.key} className="col-6 col-md-3 d-flex">
                <SupportCard>
                  {card.icon}
                  <div className="support-body">
                    <p className="small text-muted mb-1">{card.label}</p>
                    <p className="fw-semibold small mb-0 text-truncate w-100 px-1">{card.detail}</p>
                  </div>
                  <div className="support-action mt-3">
                    <ButtonComp
                      btnText={card.btnText}
                      onClick={() => openSupport(card.type)}
                      {...investorButtonProps.outline}
                      ButtonInlineStyle={{
                        ...investorButtonProps.outline.ButtonInlineStyle,
                        minHeight: 38,
                        fontSize: "0.8rem",
                        width: "100%",
                        minWidth: 0,
                      }}
                    />
                  </div>
                </SupportCard>
              </div>
            ))}
          </div>
        </InvestmentDetailSection>
      ) : null}
    </>
  );
}
